import { Component, ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a chunk loading error
    const isChunkError =
      error.name === "ChunkLoadError" ||
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.message.includes("Loading chunk") ||
      error.message.includes("Loading CSS chunk");

    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ChunkErrorBoundary caught:", error, errorInfo);
  }

  handleReload = async () => {
    // Clear all caches
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }

    // Unregister service workers
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }

    // Hard reload with cache busting
    window.location.href = window.location.pathname + "?v=" + Date.now();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
              <RefreshCw className="h-8 w-8 text-destructive" />
            </div>
            
            <h1 className="text-2xl font-bold text-foreground">
              {this.state.isChunkError ? "Update Available" : "Something went wrong"}
            </h1>
            
            <p className="text-muted-foreground">
              {this.state.isChunkError
                ? "A new version of the app is available. Please reload to get the latest updates."
                : "An unexpected error occurred. Reloading the page may fix the issue."}
            </p>

            <Button
              onClick={this.handleReload}
              size="lg"
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Reload App
            </Button>

            <p className="text-xs text-muted-foreground/70">
              This will clear cached data and reload the page.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
